import React, { useState, useEffect } from 'react';
import companyLogo from '../assets/xpass_qr_logo_new.png';
import { useAuth } from '../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { apiClient } from '../utils/apiClient';
import { LEGAL_LINKS } from '../config/legal';
import LoginBrandStage from './LoginBrandStage';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function LiveField({ icon, label, value, onChange, type = 'text', extra, autoComplete, required, autoCapitalize, autoCorrect, autoFocus, maxLength }) {
    const [focus, setFocus] = useState(false);
    const filled = Boolean(value);
    return (
        <label className={`xpass-field ${focus ? 'is-focus' : ''} ${filled ? 'is-filled' : ''}`}>
            <span className="xpass-field-icon material-symbols-outlined">{icon}</span>
            <span className="xpass-field-label">{label}</span>
            <input
                className="xpass-field-input"
                value={value}
                onChange={onChange}
                type={type}
                required={required}
                autoComplete={autoComplete}
                autoCapitalize={autoCapitalize}
                autoCorrect={autoCorrect}
                autoFocus={autoFocus}
                maxLength={maxLength}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
            />
            {extra ? <span className="xpass-field-extra">{extra}</span> : null}
            <span className="xpass-field-line" />
        </label>
    );
}

function MorphSubmitButton({ phase, disabled, label }) {
    return (
        <div className="flex w-full justify-center">
            <button
                type="submit"
                disabled={disabled || phase === 'busy' || phase === 'ok'}
                className={[
                    'xpass-morph-btn',
                    phase === 'busy' ? 'is-busy' : '',
                    phase === 'ok' ? 'is-ok' : '',
                    phase === 'err' ? 'is-err' : '',
                ].join(' ')}
            >
                <span className="xpass-morph-label">{label}</span>
                <span className="xpass-morph-spinner" aria-hidden="true" />
                <svg className="xpass-morph-check" viewBox="0 0 52 52" aria-hidden="true">
                    <path d="M14 27 L22 35 L38 17" />
                </svg>
            </button>
        </div>
    );
}

export default function TenantLoginPage() {
    const { login, mfaComplete } = useAuth();
    const [formFirmCode, setFormFirmCode] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [phase, setPhase] = useState('idle');
    const [mfaStep, setMfaStep] = useState('login');
    const [mfaTempToken, setMfaTempToken] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [mfaSetupData, setMfaSetupData] = useState(null);

    useEffect(() => {
        const savedFirm = localStorage.getItem('remember_firmCode');
        const savedUser = localStorage.getItem('remember_username');
        if (savedFirm && savedUser) {
            setFormFirmCode(savedFirm);
            setUsername(savedUser);
            setRememberMe(true);
        }
    }, []);

    const persistRemember = () => {
        if (rememberMe) {
            localStorage.setItem('remember_firmCode', formFirmCode);
            localStorage.setItem('remember_username', username);
        } else {
            localStorage.removeItem('remember_firmCode');
            localStorage.removeItem('remember_username');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formFirmCode || !username || !password) {
            setError('Lütfen tüm alanları doldurun.');
            return;
        }

        setPhase('busy');
        const started = Date.now();

        try {
            const result = await login(formFirmCode, username, password, { deferApply: true });
            const hold = 620 - (Date.now() - started);
            if (hold > 0) await wait(hold);
            if (result.success && result.mfaRequired) {
                setMfaTempToken(result.tempToken);
                if (result.isSetupRequired) {
                    setMfaStep('setup');
                    try {
                        const setupRes = await apiClient('Auth/mfa/setup', {
                            method: 'POST',
                            body: { tempToken: result.tempToken }
                        });
                        setMfaSetupData(setupRes);
                    } catch (err) {
                        setError("MFA kurulum bilgileri alınamadı.");
                        setMfaStep('login');
                    }
                } else {
                    setMfaStep('verify');
                }
                setPhase('idle');
            } else if (!result.success) {
                setError(result.message || 'Giriş başarısız.');
                setPhase('err');
                await wait(520);
                setPhase('idle');
            } else {
                persistRemember();
                setPhase('ok');
                await wait(1100);
                if (result.apply) result.apply();
            }
        } catch (err) {
            console.error("Giriş işlemi hatası:", err);
            let message = err.message || 'Giriş başarısız.';
            if (err.status === 401) message = 'Kullanıcı adı veya şifre hatalı.';
            else if (err.status === 402 || (err.code === 'LICENSE_EXPIRED') || message.includes('Lisans')) message = err.payload?.message || "Lisans süreniz dolmuştur.";
            else if (err.status === 404) message = 'Firma kodu hatalı.';
            setError(message);
            setPhase('err');
            await wait(520);
            setPhase('idle');
        }
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setPhase('busy');
        const started = Date.now();

        try {
            if (mfaStep === 'setup') {
                await apiClient('Auth/mfa/enable', {
                    method: 'POST',
                    body: { tempToken: mfaTempToken, code: mfaCode }
                });
            } else if (mfaStep === 'verify' && !mfaTempToken) {
                throw new Error("Geçici oturum anahtarı bulunamadı. Lütfen tekrar giriş yapın (Sayfayı yenilemiş olabilirsiniz).");
            }
            const result = await mfaComplete(mfaTempToken, mfaCode, { deferApply: true });
            const hold = 620 - (Date.now() - started);
            if (hold > 0) await wait(hold);
            persistRemember();
            setPhase('ok');
            await wait(1100);
            if (result.apply) result.apply();
        } catch (err) {
            setError(err.message || 'MFA doğrulama başarısız.');
            setPhase('err');
            await wait(520);
            setPhase('idle');
        }
    };

    const busy = phase === 'busy' || phase === 'ok';

    return (
        <div className="relative flex min-h-[100dvh] w-full bg-[#0F1115] font-display text-white lg:h-screen lg:overflow-hidden">
            <div className="relative z-10 flex min-h-[100dvh] w-full lg:h-full">
                <section className="xpass-login-pane flex w-full flex-col justify-center overflow-x-hidden overflow-y-auto px-5 py-8 sm:px-8 lg:w-1/3 lg:px-8 lg:pr-14 xl:px-10 xl:pr-16">
                    <div className="mx-auto w-full max-w-[420px] lg:mx-0 lg:max-w-none">
                        <div className="xpass-login-brand mb-8">
                            <div className="xpass-login-halo">
                                <span className="xpass-login-halo-ring" aria-hidden="true" />
                                <span className="xpass-login-halo-ring xpass-login-halo-ring-b" aria-hidden="true" />
                                <span className="xpass-login-halo-glow" aria-hidden="true" />
                                <img src={companyLogo} alt="XPass" className="xpass-login-brand-logo" />
                            </div>
                            <span className="xpass-login-wordmark">XPass PDKS</span>
                        </div>

                        <h1 className="xpass-login-stagger text-[1.75rem] font-bold tracking-tight sm:text-3xl" style={{ '--i': 0 }}>
                            {mfaStep === 'login' && 'Giriş yapın'}
                            {mfaStep === 'setup' && 'MFA Kurulumu'}
                            {mfaStep === 'verify' && 'İki Adımlı Doğrulama'}
                        </h1>
                        <span className="xpass-login-title-mark" />
                        <p className="xpass-login-stagger mt-3 mb-8 text-sm text-slate-400" style={{ '--i': 1 }}>
                            {mfaStep === 'login' && 'Firma kodu, kullanıcı adı ve şifrenizle oturum açın.'}
                            {mfaStep === 'setup' && "Hesabınızı güvene almak için MFA'yı aktif edin."}
                            {mfaStep === 'verify' && 'Authenticator uygulamasındaki 6 haneli kodu girin.'}
                        </p>

                        {mfaStep === 'login' ? (
                            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                                <div className="xpass-login-stagger" style={{ '--i': 2 }}>
                                    <LiveField
                                        icon="domain"
                                        label="Firma Kodu"
                                        value={formFirmCode}
                                        onChange={(e) => setFormFirmCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                        required
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                    />
                                </div>
                                <div className="xpass-login-stagger" style={{ '--i': 3 }}>
                                    <LiveField
                                        icon="person"
                                        label="Kullanıcı Adı"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                        autoComplete="username"
                                    />
                                </div>
                                <div className="xpass-login-stagger" style={{ '--i': 4 }}>
                                    <LiveField
                                        icon="lock"
                                        label="Şifre"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        autoComplete="current-password"
                                        extra={(
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label="Şifreyi göster">
                                                <span className="material-symbols-outlined text-[20px]">{showPassword ? 'visibility' : 'visibility_off'}</span>
                                            </button>
                                        )}
                                    />
                                </div>

                                <div className="xpass-login-stagger flex items-center justify-between pt-1" style={{ '--i': 5 }}>
                                    <label className="flex cursor-pointer items-center gap-2">
                                        <input
                                            className="h-4 w-4 appearance-none rounded border border-white/15 bg-[#2B2F36] checked:border-[#e30613] checked:bg-[#e30613]"
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                        />
                                        <span className="text-sm text-slate-400">Beni Hatırla</span>
                                    </label>
                                    <a className="text-sm font-medium text-[#e30613] hover:text-red-400" href="#">Şifremi Unuttum?</a>
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
                                        <span className="material-symbols-outlined text-[18px]">error</span>
                                        {error}
                                    </div>
                                )}

                                <div className="xpass-login-stagger pt-1" style={{ '--i': 6 }}>
                                    <MorphSubmitButton phase={phase} disabled={busy} label="Giriş Yap" />
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleMfaSubmit} className="flex flex-col gap-5">
                                {mfaStep === 'setup' && mfaSetupData && (
                                    <div className="mb-2 flex flex-col items-center space-y-4 rounded-xl border border-white/10 bg-[#1a1c21] p-4 text-center">
                                        <p className="text-xs text-slate-300">Google Authenticator veya uyumlu bir uygulama ile QR kodu okutun.</p>
                                        <div className="inline-block rounded-lg bg-white p-2">
                                            <QRCodeSVG value={mfaSetupData.otpauthUri} size={150} level="Q" />
                                        </div>
                                        <p className="select-all break-all rounded-full bg-white/5 px-3 py-1.5 font-mono text-xs text-slate-300">
                                            {mfaSetupData.manualKey}
                                        </p>
                                    </div>
                                )}

                                <LiveField
                                    icon="pin"
                                    label="Doğrulama Kodu"
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ''))}
                                    required
                                    autoComplete="one-time-code"
                                    autoFocus
                                    maxLength={6}
                                />

                                {error && (
                                    <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
                                        <span className="material-symbols-outlined text-[18px]">error</span>
                                        {error}
                                    </div>
                                )}

                                <div className="mt-2 flex items-center gap-3">
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => { setMfaStep('login'); setMfaCode(''); setPhase('idle'); }}
                                        className="w-1/3 rounded-xl border border-white/10 py-[16px] font-medium text-slate-300 hover:bg-white/5"
                                    >
                                        İptal
                                    </button>
                                    <div className="w-2/3">
                                        <MorphSubmitButton phase={phase} disabled={busy || mfaCode.length < 6} label="Doğrula" />
                                    </div>
                                </div>
                            </form>
                        )}

                        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium text-slate-500">
                            <a href={LEGAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="hover:text-slate-300">Gizlilik Politikası</a>
                            <span className="text-slate-700">•</span>
                            <a href={LEGAL_LINKS.KVKK_AYDINLATMA} target="_blank" rel="noopener noreferrer" className="hover:text-slate-300">KVKK Aydınlatma Metni</a>
                            <span className="text-slate-700">•</span>
                            <a href={LEGAL_LINKS.SUPPORT} target="_blank" rel="noopener noreferrer" className="hover:text-slate-300">Destek</a>
                        </div>
                    </div>
                </section>

                <LoginBrandStage />
            </div>
        </div>
    );
}
